import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, Select, Users01 } from "@opendate-app/setlist";
import DecisionModal from "../../ClassesShared/DecisionModal";
import "../../ClassesShared/classesShared.css";

export function reassignPayload(session, roomId, instructorIds) {
  const assignments = session.instructor_assignments || [];
  const kept = new Set(instructorIds);
  const existing = new Set(assignments.map((assignment) => assignment.instructor_id));
  return {
    room_id: roomId || null,
    class_session_instructors_attributes: [
      ...assignments.filter((assignment) => !kept.has(assignment.instructor_id)).map((assignment) => ({ id: assignment.id, _destroy: true })),
      ...instructorIds.filter((id) => !existing.has(id)).map((id) => ({ instructor_id: id })),
    ],
  };
}

const FIELD_LABELS = { room: "Room", instructor: "Instructor", instructor_id: "Instructor", class_session_instructors: "Instructors" };

export function errorMessages(e) {
  const errors = e.response?.data?.errors;
  if (!errors) return ["Could not save the session."];
  if (Array.isArray(errors)) return errors;
  return Object.entries(errors).flatMap(([key, messages]) => {
    const label = FIELD_LABELS[key.split(".").pop()];
    return messages.map((message) => (label ? `${label} ${message}` : message));
  });
}

function ReassignSessionModal({ session, options, hasRegistrations, csrfToken, onClose, onSaved }) {
  const assignedIds = (session.instructor_assignments || []).map((assignment) => assignment.instructor_id);
  const [roomId, setRoomId] = useState(session.room_id ? String(session.room_id) : null);
  const [instructorIds, setInstructorIds] = useState(assignedIds);
  const [working, setWorking] = useState(false);
  const [errors, setErrors] = useState([]);

  const roomOptions = options.rooms.map((room) => ({ value: String(room.id), label: room.name }));
  const instructorOptions = options.instructors.map((instructor) => ({
    value: instructor.id,
    label: instructor.archived ? `${instructor.name} (archived)` : instructor.name,
  }));

  const submit = async () => {
    if (working) return;
    setWorking(true);
    setErrors([]);
    try {
      await axios.patch(
        session.update_path,
        { class_session: reassignPayload(session, roomId, instructorIds) },
        { headers: { "X-CSRF-Token": csrfToken } },
      );
      onSaved();
    } catch (e) {
      setErrors(errorMessages(e));
      setWorking(false);
    }
  };

  return (
    <DecisionModal
      type="brand"
      icon={Users01}
      title="Reassign Session"
      description={`Change the instructors or room for ${session.name}. To change its date or time, edit the class.`}
      confirmLabel={working ? "Saving…" : "Save"}
      onClose={onClose}
      onConfirm={submit}
      working={working}
    >
      {errors.length > 0 && <Alert color="error" role="alert" description={errors.join(" ")} className="mb-3" />}
      <div className="d-flex flex-column classes-gap-16">
        <Select
          label="Instructors"
          multiple
          searchable
          placeholder="Assign instructors…"
          options={instructorOptions}
          value={instructorIds}
          onChange={(values) => setInstructorIds(values || [])}
        />
        <Select
          label="Room"
          placeholder="No room"
          searchable
          clearable
          options={roomOptions}
          value={roomId}
          onChange={(value) => setRoomId(value || null)}
        />
        {hasRegistrations && (
          <p className="small classes-text-secondary mb-0">Changing the room emails registered attendees the updated schedule. Changing instructors doesn&apos;t.</p>
        )}
      </div>
    </DecisionModal>
  );
}

ReassignSessionModal.propTypes = {
  session: PropTypes.shape({
    name: PropTypes.string,
    room_id: PropTypes.number,
    update_path: PropTypes.string.isRequired,
    instructor_assignments: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, instructor_id: PropTypes.number })),
  }).isRequired,
  options: PropTypes.shape({
    instructors: PropTypes.array.isRequired,
    rooms: PropTypes.array.isRequired,
  }).isRequired,
  hasRegistrations: PropTypes.bool,
  csrfToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ReassignSessionModal;
